import type { ReactNode } from "react";
import { motion } from "framer-motion";
import { DURATION, EASE_OUT_SOFT, EASE_SPRING } from "../lib/motion";
import { BgOrbs } from "./BgOrbs";
import { HexAmbient } from "./HexAmbient";
import { Lockup } from "./Lockup";

/**
 * Beyond Sure — Onboarding Shell (v3.6).
 *
 * INTENTIONALLY DIFFERENT FROM AuthShell. The login + OTP screens use a
 * desktop split-screen (brand world LEFT, form RIGHT) because they're
 * marketing-leaning. Onboarding wants the user to focus on ONE question at
 * a time — so the layout is a single CENTERED COLUMN, mint canvas, with
 * the question card floating on the brand background.
 *
 * Shared with the locked auth shell:
 *   • Same fonts, palette, glass tokens, BgOrbs + HexAmbient background.
 *   • Same Framer Motion timing curves.
 *   • Same lockup at the top-left corner.
 *
 * Distinct from auth:
 *   • Mint canvas (lighter, "next chapter" feel) instead of forest green.
 *   • Centered column, single visual focus — no PhoneShowcase, no headline.
 *   • Progress dots at the very top so the user knows the length.
 *   • Back/Continue/Skip pinned to the bottom of the card.
 *
 * Slot contract:
 *   stepIndex     1-based current step
 *   totalSteps    e.g. 9 for the question flow
 *   title         The big question itself (Vercetti display style)
 *   helper        One short sentence underneath (Geist prose)
 *   children      The actual input(s) for this step
 *   onBack        Back handler. Pass null/undefined on the first step
 *                 to hide the back button.
 *   onContinue    Continue handler — disabled by parent via the prop
 *   continueLabel Label override (e.g. "Continue", "Finish", "I'll come back to this")
 *   continueDisabled  Validation gate
 *   onSkip        Optional — only shown when the step is skippable
 */
interface OnboardingShellProps {
  stepIndex: number;
  totalSteps: number;
  title: string;
  helper?: ReactNode;
  children: ReactNode;
  onBack?: (() => void) | null;
  onContinue: () => void;
  continueLabel?: string;
  continueDisabled?: boolean;
  continueLoading?: boolean;
  onSkip?: () => void;
  /** Optional muted intro line shown above the title — used by the intro
   * flow set so the eyebrow can read "What I can do for you · 1 of 4". */
  eyebrow?: string;
}

export function OnboardingShell({
  stepIndex,
  totalSteps,
  title,
  helper,
  children,
  onBack,
  onContinue,
  continueLabel = "Continue",
  continueDisabled = false,
  continueLoading = false,
  onSkip,
  eyebrow,
}: OnboardingShellProps) {
  // Entrance choreography matches the auth shell so the transition from
  // OTP success → onboarding feels like one continuous flow.
  const column = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } },
  };
  const item = {
    hidden: { opacity: 0, y: 12 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: DURATION.entrance, ease: EASE_OUT_SOFT },
    },
  };
  const cardSpring = {
    hidden: { opacity: 0, y: 18, scale: 0.985 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.65, ease: EASE_SPRING },
    },
  };

  return (
    <main
      className="bsure-motion relative min-h-[100dvh] w-full overflow-x-hidden"
      style={{ backgroundColor: "var(--color-mint)" }}
    >
      {/* Ambient — mint canvas with hexagons + soft drifting orbs.
          Same building blocks as the locked auth shell so the brand
          continuity is unmistakable. */}
      <div aria-hidden="true" className="absolute inset-0">
        <BgOrbs tone="light" intensity={0.7} />
        <HexAmbient tone="light" intensity={0.65} />
      </div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={column}
        className={[
          "relative z-10 mx-auto flex min-h-[100dvh] flex-col items-stretch",
          "max-w-[640px] px-5 sm:px-8",
          "pt-6 sm:pt-8 pb-8 sm:pb-10",
        ].join(" ")}
      >
        {/* Top row — lockup + progress dots */}
        <motion.div
          variants={item}
          className="flex items-center justify-between gap-4"
        >
          <Lockup variant="full" height={28} />
          <ProgressDots current={stepIndex} total={totalSteps} />
        </motion.div>

        {/* Spacer pushes the card toward the vertical center */}
        <div className="flex-1 min-h-[24px]" />

        {/* Question card */}
        <motion.section
          variants={cardSpring}
          aria-label={`Step ${stepIndex} of ${totalSteps}`}
          className={[
            "rounded-[var(--radius-2xl)]",
            "bsure-glass-crisp",
            "px-6 py-7 sm:px-9 sm:py-9",
            "flex flex-col gap-6",
          ].join(" ")}
        >
          {eyebrow ? (
            <motion.div
              variants={item}
              className="bsure-caps text-[10.5px] text-[var(--color-brand-deep)]"
            >
              {eyebrow}
            </motion.div>
          ) : null}

          <motion.div variants={item} className="flex flex-col gap-3">
            <h1
              className="bsure-display leading-[1.05] text-[1.875rem] sm:text-[2.25rem] text-[var(--color-ink)]"
              style={{ fontWeight: 900 }}
            >
              {title}
            </h1>
            {helper ? (
              <p className="bsure-prose text-[14px] sm:text-[14.5px] leading-[1.55] text-[var(--color-ink-60)]">
                {helper}
              </p>
            ) : null}
          </motion.div>

          {/* The actual step input(s) */}
          <motion.div variants={item} className="bsure-ui">
            {children}
          </motion.div>

          {/* Action row — Back left, Continue right (Skip below on phone) */}
          <motion.div
            variants={item}
            className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
          >
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                className="bsure-ui order-2 sm:order-1 self-start text-[13px] py-2 px-1 text-[var(--color-ink-60)] hover:text-[var(--color-ink)] transition-colors"
              >
                ← Back
              </button>
            ) : (
              <span className="order-2 sm:order-1 hidden sm:block" />
            )}

            <div className="order-1 sm:order-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              {onSkip ? (
                <button
                  type="button"
                  onClick={onSkip}
                  className="bsure-ui text-[12.5px] py-2 px-3 text-[var(--color-ink-60)] hover:text-[var(--color-ink)] transition-colors"
                >
                  Skip for now
                </button>
              ) : null}
              <button
                type="button"
                onClick={onContinue}
                disabled={continueDisabled || continueLoading}
                className={[
                  "bsure-ui font-semibold text-[14px]",
                  "rounded-[var(--radius-pill)] py-3 px-6",
                  "bg-[var(--color-ink)] text-[var(--color-paper)]",
                  "transition-all duration-[var(--duration-base)] ease-[var(--ease-out-soft)]",
                  "hover:scale-[1.02] active:scale-[0.98]",
                  "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100",
                  "inline-flex items-center justify-center gap-2",
                ].join(" ")}
              >
                {continueLoading ? "Saving…" : continueLabel}
                {!continueLoading ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path
                      d="M3 8h10M9 4l4 4-4 4"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : null}
              </button>
            </div>
          </motion.div>
        </motion.section>

        {/* Bottom spacer mirrors the top so the card feels centered */}
        <div className="flex-1 min-h-[24px]" />

        <motion.div
          variants={item}
          className="bsure-caps text-[9.5px] tracking-[0.16em] text-[var(--color-ink-40)] text-center"
        >
          Your answers are private · IRDAI-licensed advisor
        </motion.div>
      </motion.div>
    </main>
  );
}

/**
 * Internal — progress dots row at the top of the shell. The active dot is
 * a wider pill in brand-vivid; completed dots are filled in brand-deep;
 * future dots are ink-08.
 */
function ProgressDots({ current, total }: { current: number; total: number }) {
  return (
    <div
      role="progressbar"
      aria-valuenow={current}
      aria-valuemin={1}
      aria-valuemax={total}
      aria-label={`Step ${current} of ${total}`}
      className="flex items-center gap-1.5"
    >
      {Array.from({ length: total }).map((_, i) => {
        const idx = i + 1;
        const state =
          idx < current ? "done" : idx === current ? "active" : "todo";
        return (
          <span
            key={i}
            aria-hidden="true"
            className={[
              "h-[6px] rounded-full transition-all",
              "duration-[var(--duration-base)] ease-[var(--ease-out-soft)]",
              state === "active" ? "w-7" : "w-[6px]",
            ].join(" ")}
            style={{
              backgroundColor:
                state === "active"
                  ? "var(--color-brand-vivid)"
                  : state === "done"
                  ? "var(--color-brand-deep)"
                  : "var(--color-ink-08)",
            }}
          />
        );
      })}
    </div>
  );
}

export default OnboardingShell;
